// MobileBuddyMessenger.jsx
import React, { useState, useEffect, useRef } from "react";
import { collection, addDoc, onSnapshot, query, orderBy, limit, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { containsProfanity } from "../../data/profanityList";
import { isQuestion, BOT_USERNAME } from "../../data/chatBot";
import OSIcon32 from "../icons/OSIcon32";
import "./BuddyMessenger.css";

const COLORS = ["#3b5998", "#2ecc71", "#e74c3c", "#9b59b6", "#f1c40f", "#ff69b4"];

// Random username generator
const generateUsername = () => {
  const base = ["Bestie", "Sk8ter", "BFF", "Gurl", "Boi", "sk8trGurl", "bigBoi", "xxBlinkfan", "SeNsEsFaIl", "Queen", "King", "Rawr", "Xtreme", "Diva", "Angel", "Star", "Cutie",
    "xXPunk", "Scene", "Emo", "TruBlu", "Lil", "Big", "Mister", "Miss", "Mizz", "Hunni", "Snuggle", "Sugar", "Cherry",
    "Glitter", "Princess", "Bad", "HotTopic", "Dark", "Bright", "Cyber", "LoFi", "MallRat", "Dreamy", "iCandy", "HannaMontana", "b4ddie", "BADDIE",
    "Hype", "Krazy", "Funky", "Silent", "NoScope", "Drama", "krunk", "Sassy", "Jelly", "Neon", "Pixel", "Neko", "neo", "ne0petz", "uWu", "Kawaii",
    "Sleepy", "Happy", "SadBoi", "CryBaby", "xxScene", "disturbed", "Vamp", "Lolipop", "Nvrland", "SnowBunni", "Aesthetic",
    "Retro", "Myspace", "Livewire", "Blinkie", "MoodRing", "AwayMsg", "Napster", "LiveJ0urnal", "m4pl3Story", "brighteyes", "dashboard", "Dreamer", "MixTape",
    "PopRox", "HelloKitty", "Tamagotchi", "Furbz", "Slay", "Laggy", "Aimless", "Lurker", "WebcamQueen", "AltBoi"];
  const suffixes = ["4eva", "2kool", "2hot", "luv", "XOXO", "<3", "x3", "99", "hack3r", "01", "luvver", "bby", "666", "420", "1337",
    "_lol", "_omg", "OMGZ", "ROFLc0pter", "xx", "xo", "ily", "_rawr", "_grl", "zzz", "_AFK", "BRB", "IMissU", "T1m3", "xxX",
    "_roxx", "_g2g", "_ttyl", "_emo", "_cutie", "_gurl", "_punkz", "_dreamz", "_vibes", "PLZ", "OMG", "LOL",
    "_btw", "_asl", "_xo", "_jk", "_BRB", "n00b", "L8R", "_bffl", "_<333", "_v4mp", "_cringe",
    "_blinkz", "s0cute", "uWish", "justChillin", "lmao", "_squad", "_182", "_ftp", "_dialUp", "_away", "zzzZZZ", "_bored",];
  const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
  return `${rand(base)}${rand(suffixes)}`;
};

const MobileBuddyMessenger = ({ onClose }) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [username, setUsername] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");
  const [botTyping, setBotTyping] = useState(false);
  const messageEndRef = useRef(null);
  const hasScrolledInitially = useRef(false);
  const botWaitDeadlineRef = useRef(0);
  const botTypingTimeoutRef = useRef(null);

  const CHARACTER_LIMIT = 140;

  useEffect(() => {
    // No sound effects on mobile

    let sessionUsername = sessionStorage.getItem("username");
    if (!sessionUsername) {
      sessionUsername = generateUsername();
      sessionStorage.setItem("username", sessionUsername);
    }
    setUsername(sessionUsername);

    const q = query(collection(db, "guestMessages"), orderBy("timestamp", "desc"), limit(50));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messageList = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (!data.color && !data.isBot) {
            data.color = COLORS[Math.floor(Math.random() * COLORS.length)];
          }
          messageList.push({ id: doc.id, ...data });
        });
        if (messageList.length === 0) {
          setMessages([
            { username: "bigdude09", text: "Lets goo!", color: "#3b5998", timestamp: new Date() },
            { username: "beachgurXX", text: "This is awesome 😎", color: "#2ecc71", timestamp: new Date() },
          ]);
        } else {
          const ordered = messageList.reverse();
          if (botWaitDeadlineRef.current > 0) {
            const fresh = ordered.find((m) => {
              if (!m.isBot) return false;
              const ts = m.timestamp && m.timestamp.toDate ? m.timestamp.toDate().getTime() : 0;
              return ts >= botWaitDeadlineRef.current - 60000;
            });
            if (fresh) {
              botWaitDeadlineRef.current = 0;
              setBotTyping(false);
              if (botTypingTimeoutRef.current) {
                clearTimeout(botTypingTimeoutRef.current);
                botTypingTimeoutRef.current = null;
              }
            }
          }
          setMessages(ordered);
        }
      },
      (error) => {
        console.error("Error subscribing to messages:", error);
        setMessages([
          { username: "bigdude09", text: "Lets goo!", color: "#3b5998", timestamp: new Date() },
          { username: "beachgurXX", text: "This is awesome 😎", color: "#2ecc71", timestamp: new Date() },
        ]);
      },
    );

    return () => {
      unsubscribe();
      if (botTypingTimeoutRef.current) clearTimeout(botTypingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (messages.length === 0 && !botTyping) return;
    const behavior = hasScrolledInitially.current ? "smooth" : "auto";
    messageEndRef.current?.scrollIntoView({ behavior, block: "end" });
    hasScrolledInitially.current = true;
  }, [messages, botTyping]);

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    setCharCount(e.target.value.length);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setError("Please enter a message.");
      return;
    }
    if (message.length > CHARACTER_LIMIT) {
      setError(`Message too long. Please keep it under ${CHARACTER_LIMIT} characters.`);
      return;
    }
    if (containsProfanity(message)) {
      setError("Your message contains inappropriate language. Please revise it.");
      return;
    }

    try {
      // Reuse last color if same user
      const lastMessage = messages[messages.length - 1];
      const messageColor = (lastMessage && lastMessage.username === username)
        ? lastMessage.color
        : COLORS[Math.floor(Math.random() * COLORS.length)];

      await addDoc(collection(db, "guestMessages"), {
        text: message,
        username: username,
        timestamp: serverTimestamp(),
        color: messageColor
      });

      if (isQuestion(message)) {
        botWaitDeadlineRef.current = Date.now();
        setBotTyping(true);
        if (botTypingTimeoutRef.current) clearTimeout(botTypingTimeoutRef.current);
        botTypingTimeoutRef.current = setTimeout(() => {
          setBotTyping(false);
          botWaitDeadlineRef.current = 0;
          botTypingTimeoutRef.current = null;
        }, 20000);
      }

      // No sound effects on mobile

      setMessage("");
      setCharCount(0);
      setError("");
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Couldn't send your message. Please try again later.");
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : timestamp;
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${month}/${day}`;
  };

  return (
    <div 
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000
      }}
      onClick={onClose}
    >
      {/* No audio elements on mobile */}

      <div
        className="retro-window win95-window"
        style={{
          width: "90%",
          maxWidth: "400px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontFamily: "var(--font-content)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Chat title bar — gradient chrome, matches desktop windows */}
        <div className="win95-titlebar" style={{ cursor: "default", flex: "none" }}>
          <OSIcon32 kind="messenger" size={15} className="win95-titlebar__icon" />
          <span className="win95-titlebar__label">Buddy Messenger</span>
          <div className="win95-titlebar__btns">
            <button
              type="button"
              className="win95-btn-sys win95-close-btn"
              onClick={onClose}
              aria-label="Close window"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Message window */}
        <div
          style={{
            background: "var(--mb-paper)",
            border: "2px solid var(--mb-ink)",
            height: "min(250px, 32dvh)",
            overflowY: "auto",
            overscrollBehavior: "contain",
            padding: "8px",
            fontSize: "12px",
            margin: "8px",
            color: "var(--mb-ink)"
          }}
        >
          {messages.map((msg, index) => (
            <div key={msg.id || index} style={{ marginBottom: "6px", display: "flex", flexWrap: "wrap" }}>
              {msg.isBot && (
                <span
                  style={{
                    background: "var(--mb-grape)",
                    color: "var(--mb-paper)",
                    fontFamily: "var(--font-chrome)",
                    fontSize: "9px",
                    lineHeight: 1.4,
                    padding: "1px 4px",
                    marginRight: "4px",
                    letterSpacing: "0.04em",
                    alignSelf: "flex-start",
                  }}
                >
                  Mod
                </span>
              )}
              <span style={{ color: msg.isBot ? "var(--mb-grape)" : (msg.color || "var(--mb-ink)"), fontWeight: "bold" }}>
                {msg.username}:
              </span>
              <span style={{ marginLeft: "4px", wordBreak: "break-word", flex: "1" }}>
                {msg.text}
              </span>
              <span style={{ marginLeft: "8px", color: "var(--mb-ink-60)", fontSize: "10px" }}>
                [{formatTimestamp(msg.timestamp)}]
              </span>
            </div>
          ))}
          {botTyping && (
            <div style={{ marginBottom: "6px", display: "flex", flexWrap: "wrap" }}>
              <span
                style={{
                  background: "var(--mb-grape)",
                  color: "var(--mb-paper)",
                  fontFamily: "var(--font-chrome)",
                  fontSize: "9px",
                  lineHeight: 1.4,
                  padding: "1px 4px",
                  marginRight: "4px",
                  letterSpacing: "0.04em",
                  alignSelf: "flex-start",
                }}
              >
                Mod
              </span>
              <span style={{ color: "var(--mb-grape)", fontWeight: "bold" }}>
                {BOT_USERNAME}:
              </span>
              <span style={{ marginLeft: "4px", fontStyle: "italic", color: "var(--mb-ink-60)" }}>
                typing<span className="typing-dot">.</span><span className="typing-dot">.</span><span className="typing-dot">.</span>
              </span>
            </div>
          )}
          <div ref={messageEndRef} />
        </div>

        {/* Message input area */}
        <div style={{ padding: "0 8px 5px 8px" }}>
          <textarea
            value={message}
            onChange={handleMessageChange}
            placeholder="Leave us a message, (PS These are public.)"
            maxLength={CHARACTER_LIMIT}
            style={{
              width: "100%",
              height: "60px",
              border: "2px solid var(--mb-ink)",
              padding: "6px",
              fontSize: "16px", /* 16px floor — smaller triggers iOS zoom-on-focus */
              fontFamily: "var(--font-content)",
              resize: "none",
              boxSizing: "border-box",
              marginBottom: "5px",
              background: "var(--mb-chalk)",
              color: "var(--mb-ink)"
            }}
          />
        </div>

        {/* Message footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 8px 10px 8px",
            fontSize: "12px"
          }}
        >
          <div style={{ color: "var(--mb-ink-60)" }}>
            {CHARACTER_LIMIT - charCount} characters left
          </div>
          {error && (
            <div style={{ color: "var(--mb-magenta-deep)", textAlign: "center", flexGrow: 1, padding: "0 10px", fontWeight: "bold" }}>
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            className="win95-btn win95-btn-primary send-button"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileBuddyMessenger;